import { useEffect, useState } from 'react';
import { ExternalLink, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '../components/ui/sheet';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import {
  AlertDialog, AlertDialogTrigger, AlertDialogContent, AlertDialogHeader,
  AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction,
} from '../components/ui/alert-dialog';
import { OTCOrderCard } from '../portal/shop/OTCOrderCard';
import StaffAppRecallSheet from './StaffAppRecallSheet';

/** One-tap-with-confirm action: a destructive/final button that always
 * asks "are you sure?" before it fires — matches the product requirement
 * that every approve/reject needs one explicit confirm tap. */
export function ConfirmButton({ label, title, description, onConfirm, loading, variant = 'default', testId }) {
  return (
    <AlertDialog>
      <AlertDialogTrigger asChild>
        <Button variant={variant} className="flex-1 h-11" disabled={loading} data-testid={testId}>
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : label}
        </Button>
      </AlertDialogTrigger>
      <AlertDialogContent data-testid={`${testId}-confirm-dialog`}>
        <AlertDialogHeader>
          <AlertDialogTitle>{title}</AlertDialogTitle>
          <AlertDialogDescription>{description}</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel data-testid={`${testId}-confirm-cancel`}>Cancel</AlertDialogCancel>
          <AlertDialogAction onClick={onConfirm} data-testid={`${testId}-confirm-ok`}>Yes, {label.toLowerCase()}</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

function RefillActions({ item, onClose, onActed }) {
  const [loading, setLoading] = useState('');
  const act = async (action) => {
    setLoading(action);
    try {
      await api.post(`/staff/portal/refills/${item.ref_id}/decision`, { action });
      toast.success(action === 'approve' ? 'Refill approved' : 'Refill rejected');
      onActed(); onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not update this refill');
    } finally { setLoading(''); }
  };
  return (
    <div className="space-y-3">
      {item.dose_warning && (
        <div className="rounded-lg border border-red-300 bg-red-50 text-red-800 text-sm px-3 py-2"
          data-testid="staff-app-refill-dose-warning">
          <strong>Child dose check:</strong> {item.dose_warning}
        </div>
      )}
      <div className="flex gap-2">
        <ConfirmButton label="Reject" variant="outline" loading={loading === 'reject'} testId="staff-app-refill-reject"
          title="Reject this refill request?" description="The patient will be notified that it was not approved."
          onConfirm={() => act('reject')} />
        <ConfirmButton label="Approve" loading={loading === 'approve'} testId="staff-app-refill-approve"
          title="Approve this refill request?" description="The patient will be notified it's approved and being prepared."
          onConfirm={() => act('approve')} />
      </div>
    </div>
  );
}

function AdrActions({ item, onClose, onActed }) {
  const [note, setNote] = useState('');
  const [loading, setLoading] = useState(false);
  const act = async () => {
    setLoading(true);
    try {
      await api.post(`/staff/portal/adverse-reactions/${item.ref_id}/acknowledge`, { note: note.trim() || undefined });
      toast.success('Reaction report acknowledged');
      onActed(); onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not acknowledge this report');
    } finally { setLoading(false); }
  };
  return (
    <div className="space-y-3">
      <Textarea placeholder="Optional note (e.g. advice given, follow-up needed)…" value={note}
        onChange={(e) => setNote(e.target.value)} rows={3} data-testid="staff-app-adr-note" />
      <ConfirmButton label="Acknowledge" testId="staff-app-adr-acknowledge" onConfirm={act} loading={loading}
        title="Acknowledge this reaction report?" description="Marks it reviewed. You can still follow up with the patient separately." />
    </div>
  );
}

function OtcActions({ item, onActed }) {
  const [order, setOrder] = useState(null);
  const [notFound, setNotFound] = useState(false);

  const load = () => api.get('/staff/portal/otc/orders').then((res) => {
    const found = (res.data.orders || []).find((o) => o.order_id === item.ref_id);
    setOrder(found || null);
    setNotFound(!found);
  }).catch(() => setNotFound(true));

  useEffect(() => { load(); }, [item.ref_id]); // eslint-disable-line react-hooks/exhaustive-deps

  if (notFound) return <p className="text-sm text-muted-foreground py-6 text-center">Could not load this order — it may have just been updated. Pull to refresh the feed.</p>;
  if (!order) return <Loader2 className="w-5 h-5 animate-spin text-muted-foreground mx-auto my-6" />;
  return <OTCOrderCard order={order} api={api} staff refresh={() => { load(); onActed(); }} />;
}

function ReadOnlySummary({ item }) {
  const isRoleRestricted = item.action === 'approve_reject' && !item.can_act;
  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">
        {isRoleRestricted
          ? 'Only a pharmacist or manager can act on this from the phone.'
          : 'Full details and actions for this are on the desktop app.'}
      </p>
      <a href={item.deep_link} target="_blank" rel="noreferrer" data-testid="staff-app-item-desktop-link"
        className="flex items-center justify-center gap-2 h-11 rounded-md border border-border font-semibold text-sm hover:bg-muted transition-colors">
        Finish on desktop <ExternalLink className="w-3.5 h-3.5" />

      </a>
    </div>
  );
}

/** Bottom sheet that dispatches to the right action (or a read-only
 * fallback) based on the tapped feed item's category/role. */
export default function StaffAppItemDetail({ item, onClose, onActed }) {
  if (!item) return null;
  const actionable = item.action === 'approve_reject' && item.can_act;
  return (
    <Sheet open={!!item} onOpenChange={(open) => { if (!open) onClose(); }}>
      <SheetContent side="bottom" className="max-h-[88vh] overflow-y-auto rounded-t-2xl" data-testid="staff-app-item-detail">
        <SheetHeader className="text-left mb-4">
          <SheetTitle>{item.title}</SheetTitle>
          <SheetDescription>{item.subtitle}</SheetDescription>
        </SheetHeader>
        {item.action === 'recall_pull' && (item.can_act ? <StaffAppRecallSheet /> : <ReadOnlySummary item={item} />)}
        {actionable && item.category === 'refill' && <RefillActions item={item} onClose={onClose} onActed={onActed} />}
        {actionable && item.category === 'adr' && <AdrActions item={item} onClose={onClose} onActed={onActed} />}
        {actionable && item.category === 'otc_order' && <OtcActions item={item} onClose={onClose} onActed={onActed} />}
        {(item.action === 'tap_through' || (item.action === 'approve_reject' && !item.can_act)) && <ReadOnlySummary item={item} />}
      </SheetContent>
    </Sheet>
  );
}
